import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Mail, X, ShieldCheck, Star, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { useBookingModal } from '../context/BookingContext';
import { contactData } from '../data/mock';
import { trackEvent } from '../lib/track';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BookingModal — embeds the WriteUpp self-booking page (kp-physiotherapy.selectandbook.com)
 * in a large modal dialog. Triggered from anywhere in the app via the BookingContext.
 *
 * CRO additions:
 *  - Reassurance strip (rescheduling policy, rating, price anchor) at the decision point.
 *  - Gentle abandonment fallback: first time the modal is dismissed in a session, a small
 *    card offers WhatsApp or the email guide instead of losing the visitor silently.
 */
const BookingModal = () => {
  const { isOpen, intent, closeBooking } = useBookingModal();
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackEmail, setFallbackEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const meta = intent?.meta || null;

  // Global Escape handler — Radix's onEscapeKeyDown only fires when focus
  // is inside the DialogContent. Once the visitor clicks into the WriteUpp
  // iframe (cross-origin) keyboard events are trapped INSIDE that iframe
  // and never reach our parent document — a browser security boundary we
  // can't cross. This listener catches Escape while focus is still on the
  // trigger / anywhere in our own document. The X button + click-outside
  // remain the reliable close paths once the visitor is interacting with
  // the WriteUpp booking flow.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.stopPropagation();
        e.preventDefault();
        closeBooking();
      }
    };
    document.addEventListener('keydown', onKey, true);
    // On any pointer move outside the iframe, pull focus back to the
    // body so a subsequent Escape reaches our document listener.
    const restoreFocus = (e) => {
      const target = e.target;
      if (target && target.tagName !== 'IFRAME') {
        try { document.body.focus({ preventScroll: true }); } catch { /* ignore */ }
      }
    };
    document.addEventListener('mousemove', restoreFocus);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousemove', restoreFocus);
    };
  }, [isOpen, closeBooking]);

  const handleClose = () => {
    closeBooking();
    trackEvent('booking_modal_abandoned',
      meta ? `abandoned · category=${intent.category}` : 'modal closed');
    try {
      if (!sessionStorage.getItem('bookingFallbackShown')) {
        sessionStorage.setItem('bookingFallbackShown', '1');
        setShowFallback(true);
        setTimeout(() => setShowFallback(false), 45000);
      }
    } catch {
      /* sessionStorage unavailable — skip fallback */
    }
  };

  const handleFallbackWhatsApp = () => {
    trackEvent('booking_fallback_whatsapp');
    window.open(
      `https://wa.me/${contactData.whatsapp}?text=${encodeURIComponent("Hi Krina, I was about to book but have a quick question first…")}`,
      '_blank',
      'noopener'
    );
    setShowFallback(false);
  };

  const handleFallbackSubscribe = async (e) => {
    e.preventDefault();
    if (!fallbackEmail.trim()) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: fallbackEmail.trim(),
        consent: true,
        source: 'booking-fallback',
      });
      trackEvent('booking_fallback_subscribe');
      toast.success("You're in — jaw-pain tips and clinic updates are on their way.");
      setShowFallback(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Please enter a valid email address.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <DialogContent
          className="booking-modal-content"
          data-testid="booking-modal"
        >
          <DialogHeader className="booking-modal-header">
            <DialogTitle className="booking-modal-title">
              {meta ? `Book: ${meta.label}` : 'Book Your Consultation'}
            </DialogTitle>
            <DialogDescription className="booking-modal-desc">
              {meta
                ? `${meta.headline}. Pick a date and time below — secured by WriteUpp.`
                : 'Choose a date and time that works for you. Secured by WriteUpp.'}
            </DialogDescription>
            <div className="booking-modal-reassure" data-testid="booking-reassurance-strip">
              <span className="booking-reassure-chip">
                <Clock size={12} /> Free rescheduling up to 48h before
              </span>
              <span className="booking-reassure-chip">
                <Star size={12} /> 4.9★ on Google &amp; Doctify
              </span>
              <span className="booking-reassure-chip">
                <ShieldCheck size={12} /> Initial Assessment · 55 mins · £295
              </span>
              <a
                href="/fees"
                target="_blank"
                rel="noopener noreferrer"
                className="booking-reassure-link"
                data-testid="booking-fees-link"
              >
                Full price list
              </a>
            </div>
          </DialogHeader>

          <div className="booking-modal-iframe-wrapper">
            {isOpen ? (
              <iframe
                src="https://kp-physiotherapy.selectandbook.com"
                title="Book a consultation – The TMD Physio"
                loading="lazy"
                className="booking-modal-iframe"
                allow="payment"
                data-testid="booking-iframe"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {showFallback ? (
        <aside className="booking-fallback-card" data-testid="booking-fallback-card" role="dialog" aria-label="Not ready to book yet?">
          <button
            type="button"
            className="booking-fallback-close"
            onClick={() => setShowFallback(false)}
            aria-label="Dismiss"
            data-testid="booking-fallback-close"
          >
            <X size={14} />
          </button>
          <p className="booking-fallback-title">Not ready to book just yet?</p>
          <p className="booking-fallback-text">
            No pressure — ask Krina anything on WhatsApp, or get our free jaw-pain
            relief tips by email.
          </p>
          <button
            type="button"
            className="booking-fallback-whatsapp"
            onClick={handleFallbackWhatsApp}
            data-testid="booking-fallback-whatsapp"
          >
            <MessageCircle size={15} /> WhatsApp a question
          </button>
          <form className="booking-fallback-form" onSubmit={handleFallbackSubscribe}>
            <input
              type="email"
              value={fallbackEmail}
              onChange={(e) => setFallbackEmail(e.target.value)}
              placeholder="you@email.com"
              aria-label="Email address for jaw-pain tips"
              data-testid="booking-fallback-email"
              required
            />
            <button type="submit" disabled={subscribing} data-testid="booking-fallback-subscribe">
              <Mail size={14} /> {subscribing ? 'Sending…' : 'Send tips'}
            </button>
          </form>
        </aside>
      ) : null}
    </>
  );
};

export default BookingModal;
